import { Card, Avatar, Badge, Button, Text, Group, Paper } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { Link } from '@inertiajs/react';

interface Props {
  user: {
    created_at: string;
    username: string;
    avatar_url?: string;
    role_names: string[];
    role_ids?: string[];
    bookmarked_count: number;
    events_count: number;
    artists: Array<{ id: number; name: string }>;
    claimed_artist?: { id: number; name: string; avatar_url?: string };
    organizers?: Array<{
      id: number;
      name: string;
      created_at: string;
      upcoming_events_count: number;
      past_events_count: number;
      subscribers_count: number;
    }>;
  };
  onEdit: () => void;
}

export default function DashboardOverview({ user, onEdit }: Props) {
  const since = new Date(user.created_at).toLocaleDateString();
  return (
    <Card mb="lg" shadow="sm" p="lg" radius="md" style={{ backgroundColor: '#75757590' }}>
      {/* top row: avatar, username, since, edit button */}
      <Group justify="space-between" mb="md">
        <Group>
          <Avatar src={user.avatar_url} alt={user.username} size="xl" radius="50%" />
          <div>
            <Text fw={700} c="white" style={{ fontSize: '1.5rem' }}>
              {user.username}
            </Text>
            <Text size="sm" c="#ffffff90">
              User since {since}
            </Text>
          </div>
        </Group>
        {/* <Button onClick={onEdit}>Edit Profile</Button> */}
      </Group>

      {/* roles */}
      <Group gap="xs" mb="2rem">
        {user.role_names.map((name) => (
          <Badge
            key={name}
            className="tag-badge"
            styles={{
              root: {
                textTransform: 'none',
              },
            }}
          >
            {name}
          </Badge>
        ))}
      </Group>

      {/* bookmarked events */}
      <Text fw={600} size="xl" mb="sm" c="white" className="rubik">
        Your Bookmarked Events
      </Text>
      <Paper p="md" mb="md" style={{ backgroundColor: '#17171750', width: '100%' }}>
        <Group justify="space-between" align="center" style={{ width: '100%' }}>
          <Text
            component={Link}
            href={`/users/${user.username}/user-settings?section=bookmarkedEvents`}
            variant="light"
            c="white"
          >
            You have {user.bookmarked_count} bookmarked events.
          </Text>

          <Button
            component={Link}
            href={`/users/${user.username}/user-settings?section=bookmarkedEvents`}
            style={{ marginLeft: 'auto' }}
          >
            View Bookmarked Events
          </Button>
        </Group>
      </Paper>

      {/* events */}
      <Text fw={600} size="xl" mb="sm" c="white" className="rubik">
        Your Events
      </Text>
      <Paper p="md" mb="md" style={{ backgroundColor: '#17171750', width: '100%' }}>
        <Group justify="space-between" align="center" style={{ width: '100%' }}>
          <Text c="white">You have created {user.events_count} events.</Text>
          <Button component={Link} href={`/users/${user.username}/managed-events`} style={{ marginLeft: 'auto' }}>
            Manage Events
          </Button>
        </Group>
      </Paper>

      {/* artist logic */}

      {user.role_ids?.includes('Artist') && user.artists.length === 0 && (
        <Button
          component={Link}
          href={`/suggest-artist`}
          leftSection={<IconPlus size={16} />}
          mb="md"
          style={{ marginLeft: 'auto' }}
        >
          Add Artist
        </Button>
      )}

      {user.claimed_artist && (
        <>
          <Text fw={600} size="xl" mb="sm" c="white" className="rubik">
            Your Claimed Artist
          </Text>
          <Paper p="md" mb="md" withBorder style={{ backgroundColor: '#17171750' }}>
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <Group justify="flex-start" align="center" style={{ width: '100%', flexWrap: 'nowrap' }}>
                <Avatar src={user.claimed_artist.avatar_url} radius="xl" />
                <Text c="white">{user.claimed_artist.name}</Text>
              </Group>
              <Button component={Link} href={`/users/${user.username}/managed-artists`} style={{ marginLeft: 'auto' }}>
                Manage Claimed Artist
              </Button>
            </Group>
          </Paper>
        </>
      )}

      {user.artists.length > 0 && (
        <>
          <Text fw={600} size="xl" mb="sm" c="white" className="rubik">
            Your Artists
          </Text>
          <Paper p="md" mb="md" style={{ backgroundColor: '#17171750' }}>
            <Group justify="space-between" align="center" style={{ width: '100%' }}>
              <Text c="white">
                You have added {user.artists.length} artist{user.artists.length > 1 ? 's' : ''}.
              </Text>
              <Button
                component={Link}
                href={`/users/${user.username}/managed-artists`}
                style={{ marginLeft: 'auto' }}
                className="mt-2"
              >
                Manage Artists
              </Button>
            </Group>
          </Paper>
        </>
      )}

      {/* consolidated organizer section */}
      <Text fw={600} size="xl" mb="sm" c="white" className="rubik">
        Your Organizers
      </Text>
      {(user.organizers ?? []).length > 0 && (
        <Paper p="md" mb="md" style={{ backgroundColor: '#17171750', width: '100%' }}>
          {user.organizers?.map((org) => (
            <Group key={org.id} justify="space-between" align="flex-start" mb="sm" style={{ width: '100%' }}>
              <div>
                <Text fw={700} c="white">
                  {org.name}
                </Text>
                <Text size="sm" c="#f2f2f290">
                  Created {new Date(org.created_at).toLocaleDateString()}
                </Text>
                <Group gap="xs" mt="0">
                  <Text size="sm" c="white">
                    {org.upcoming_events_count} Upcoming
                  </Text>
                  <Text size="sm" c="white">
                    {org.past_events_count} Past
                  </Text>
                  <Text size="sm" c="white">
                    {org.subscribers_count} Subs
                  </Text>
                </Group>
              </div>
            </Group>
          ))}

          <Group justify="right" mt="md" style={{ width: '100%' }}>
            <Button component={Link} href={`/users/${user.username}/manage-organizations`}>
              {(user.organizers?.length ?? 0) === 1 ? 'Manage Organizer' : 'Manage Organizers'}
            </Button>
          </Group>
        </Paper>
      )}
    </Card>
  );
}
